import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import billingAPI from '../../../api/billing';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n ?? 0);

const pct = (num, denom) =>
  denom > 0 ? ((num / denom) * 100).toFixed(1) : '0.0';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (d) =>
  new Date(Date.now() - d * 86400000).toISOString().split('T')[0];

const STATUS_COLORS = {
  Paid: 'bg-green-100 text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Overdue: 'bg-red-100 text-red-700',
  'Partially Paid': 'bg-blue-100 text-blue-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

// ─── sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, sub, color = 'text-[#2D869C]' }) => (
  <div className="bg-white rounded-xl border-2 border-gray-100 shadow-sm p-5 flex flex-col gap-1">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-[#2D869C]/10 ${color} mb-1`}>
      <Icon name={icon} size={20} />
    </div>
    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-400">{sub}</p>}
  </div>
);

const SectionHeader = ({ title, icon }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon name={icon} size={18} className="text-[#2D869C]" />
    <h2 className="text-base font-bold text-gray-800">{title}</h2>
  </div>
);

// ─── main component ──────────────────────────────────────────────────────────

const FinanceBillingTab = () => {
  // ── date range ────────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(today());

  // ── data ──────────────────────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicePagination, setInvoicePagination] = useState({ page: 1, pages: 1, total: 0 });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('overview'); // overview | invoices
  const [statusFilter, setStatusFilter] = useState('');
  const [invoicePage, setInvoicePage] = useState(1);
  const [expandedDoctor, setExpandedDoctor] = useState(null);
  const [error, setError] = useState('');

  // ── date quick picks ──────────────────────────────────────────────────────
  const quickRanges = [
    { label: 'Today', from: today(), to: today() },
    { label: '7 days', from: daysAgo(7), to: today() },
    { label: '30 days', from: daysAgo(30), to: today() },
    { label: '90 days', from: daysAgo(90), to: today() },
    { label: 'This year', from: `${new Date().getFullYear()}-01-01`, to: today() },
  ];

  // ── fetch summary + doctor revenue ───────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Convert YYYY-MM-DD to full ISO strings the backend expects
      const from = fromDate ? new Date(fromDate + 'T00:00:00').toISOString() : undefined;
      const to = toDate ? new Date(toDate + 'T23:59:59').toISOString() : undefined;
      const [revData, statsData] = await Promise.all([
        billingAPI.getDoctorRevenue({ fromDate: from, toDate: to }),
        billingAPI.getStats({ fromDate: from, toDate: to }),
      ]);

      // doctor revenue response shape: { summary, doctors }
      if (revData?.summary) setSummary(revData.summary);
      else if (statsData) setSummary(statsData);

      if (revData?.doctors) setDoctors(revData.doctors);
    } catch (err) {
      console.error('Finance overview error:', err);
      setError('Failed to load finance data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // ── fetch invoices ────────────────────────────────────────────────────────
  const fetchInvoices = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const from = fromDate ? new Date(fromDate + 'T00:00:00').toISOString() : undefined;
      const to = toDate ? new Date(toDate + 'T23:59:59').toISOString() : undefined;
      const params = { fromDate: from, toDate: to, page: invoicePage, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      const data = await billingAPI.listInvoices(params);
      const items = data?.items ?? data ?? [];
      setInvoices(Array.isArray(items) ? items : []);
      if (data?.pagination) setInvoicePagination(data.pagination);
    } catch (err) {
      console.error('Invoice fetch error:', err);
      setInvoices([]);
    } finally {
      setInvoiceLoading(false);
    }
  }, [fromDate, toDate, invoicePage, statusFilter]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => {
    if (activeSection === 'invoices') fetchInvoices();
  }, [activeSection, fetchInvoices]);

  // ── helpers ───────────────────────────────────────────────────────────────
  const applyQuick = ({ from, to }) => {
    setFromDate(from);
    setToDate(to);
    setInvoicePage(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* ── Date Filter Bar ── */}
      <div className="bg-white rounded-xl border-2 border-gray-100 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-gray-500 font-semibold block mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            max={toDate}
            onChange={(e) => { setFromDate(e.target.value); setInvoicePage(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#2D869C]"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-semibold block mb-1">To</label>
          <input
            type="date"
            value={toDate}
            min={fromDate}
            max={today()}
            onChange={(e) => { setToDate(e.target.value); setInvoicePage(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#2D869C]"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {quickRanges.map((q) => (
            <button
              key={q.label}
              onClick={() => applyQuick(q)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                fromDate === q.from && toDate === q.to
                  ? 'bg-[#2D869C] text-white border-[#2D869C]'
                  : 'border-gray-200 text-gray-600 hover:border-[#2D869C] hover:text-[#2D869C]'
              }`}
            >
              {q.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => { fetchOverview(); if (activeSection === 'invoices') fetchInvoices(); }}
          className="ml-auto flex items-center gap-1.5 bg-[#2D869C] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#246b7e] transition-colors"
        >
          <Icon name="RefreshCw" size={14} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}

      {/* ── Section Toggle ── */}
      <div className="flex gap-2">
        {['overview', 'invoices'].map((sec) => (
          <button
            key={sec}
            onClick={() => setActiveSection(sec)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold border-2 transition-all capitalize ${
              activeSection === sec
                ? 'bg-[#2D869C] text-white border-[#2D869C]'
                : 'border-gray-200 text-gray-600 hover:border-[#2D869C] hover:text-[#2D869C]'
            }`}
          >
            {sec === 'overview' ? 'Finance Overview' : 'Invoice List'}
          </button>
        ))}
      </div>

      {/* ══════════════════ OVERVIEW SECTION ══════════════════ */}
      {activeSection === 'overview' && (
        <>
          {/* Stats Cards */}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-100 animate-pulse rounded-xl h-32" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon="TrendingUp"
                label="Total Revenue Billed"
                value={fmt(summary?.totalRevenue ?? summary?.totalAmount)}
                sub={`${summary?.totalInvoices ?? 0} invoices`}
              />
              <StatCard
                icon="CheckCircle"
                label="Amount Collected"
                value={fmt(summary?.totalCollected)}
                sub={`${summary?.paidInvoices ?? 0} paid invoices`}
                color="text-green-600"
              />
              <StatCard
                icon="Clock"
                label="Amount Pending"
                value={fmt(summary?.totalPending)}
                sub={`${summary?.pendingInvoices ?? 0} outstanding`}
                color="text-yellow-600"
              />
              <StatCard
                icon="XCircle"
                label="Cancelled"
                value={summary?.cancelledInvoices ?? 0}
                sub="invoices cancelled"
                color="text-gray-500"
              />
            </div>
          )}

          {/* Collection Rate */}
          {!loading && summary && (
            <div className="bg-white rounded-xl border-2 border-gray-100 shadow-sm p-5">
              <SectionHeader title="Collection Rate" icon="Activity" />
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-4 bg-gradient-to-r from-[#2D869C] to-[#3da8c9] rounded-full transition-all duration-700"
                    style={{
                      width: `${pct(
                        summary.totalCollected,
                        summary.totalRevenue ?? summary.totalAmount
                      )}%`
                    }}
                  />
                </div>
                <span className="text-lg font-bold text-[#2D869C] min-w-[60px] text-right">
                  {pct(summary.totalCollected, summary.totalRevenue ?? summary.totalAmount)}%
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {fmt(summary.totalCollected)} collected out of {fmt(summary.totalRevenue ?? summary.totalAmount)} billed
              </p>
            </div>
          )}

          {/* Doctor-wise Revenue Table */}
          <div className="bg-white rounded-xl border-2 border-gray-100 shadow-sm p-5">
            <SectionHeader title="Doctor-wise Revenue Breakdown" icon="UserCheck" />

            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-gray-100 animate-pulse rounded-lg h-12" />
                ))}
              </div>
            ) : doctors.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Icon name="Users" size={40} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">No doctor revenue data for this period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-3 text-gray-500 font-semibold">Doctor</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Invoices</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Total Billed</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Collected</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Pending</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Consult Fees</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Collection %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.map((doc) => {
                      const collPct = pct(doc.totalCollected, doc.totalBilled);
                      const isExpanded = expandedDoctor === String(doc.doctorId);
                      return (
                        <React.Fragment key={String(doc.doctorId)}>
                          <tr
                            className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => setExpandedDoctor(isExpanded ? null : String(doc.doctorId))}
                          >
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-[#2D869C]/10 flex items-center justify-center text-[#2D869C] font-bold text-xs uppercase">
                                  {(doc.doctorName || 'Dr').trim().charAt(0)}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-800">{(doc.doctorName || '').trim() || 'Unknown Doctor'}</p>
                                  {doc.specialization && (
                                    <p className="text-xs text-gray-400">{doc.specialization}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-right text-gray-700">{doc.invoiceCount}</td>
                            <td className="py-3 px-3 text-right font-semibold text-gray-900">{fmt(doc.totalBilled)}</td>
                            <td className="py-3 px-3 text-right text-green-700 font-semibold">{fmt(doc.totalCollected)}</td>
                            <td className="py-3 px-3 text-right text-yellow-700 font-semibold">{fmt(doc.totalPending)}</td>
                            <td className="py-3 px-3 text-right text-[#2D869C] font-semibold">{fmt(doc.consultationFeesBilled)}</td>
                            <td className="py-3 px-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-1.5 bg-[#2D869C] rounded-full"
                                    style={{ width: `${collPct}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold text-gray-700 w-10 text-right">{collPct}%</span>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-[#2D869C]/5">
                              <td colSpan={7} className="px-6 py-3">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                                    <p className="text-gray-500">Paid Invoices</p>
                                    <p className="text-lg font-bold text-green-600">{doc.paidCount}</p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                                    <p className="text-gray-500">Outstanding Invoices</p>
                                    <p className="text-lg font-bold text-yellow-600">{doc.pendingCount}</p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                                    <p className="text-gray-500">Avg per Invoice</p>
                                    <p className="text-lg font-bold text-[#2D869C]">
                                      {fmt(doc.invoiceCount > 0 ? doc.totalBilled / doc.invoiceCount : 0)}
                                    </p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                                    <p className="text-gray-500">Department</p>
                                    <p className="text-lg font-bold text-gray-700">{doc.department || '—'}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {/* Totals footer */}
                  {doctors.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50 font-bold">
                        <td className="py-3 px-3 text-gray-800">Total ({doctors.length} doctors)</td>
                        <td className="py-3 px-3 text-right text-gray-700">
                          {doctors.reduce((s, d) => s + (d.invoiceCount || 0), 0)}
                        </td>
                        <td className="py-3 px-3 text-right text-gray-900">
                          {fmt(doctors.reduce((s, d) => s + (d.totalBilled || 0), 0))}
                        </td>
                        <td className="py-3 px-3 text-right text-green-700">
                          {fmt(doctors.reduce((s, d) => s + (d.totalCollected || 0), 0))}
                        </td>
                        <td className="py-3 px-3 text-right text-yellow-700">
                          {fmt(doctors.reduce((s, d) => s + (d.totalPending || 0), 0))}
                        </td>
                        <td className="py-3 px-3 text-right text-[#2D869C]">
                          {fmt(doctors.reduce((s, d) => s + (d.consultationFeesBilled || 0), 0))}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════════════ INVOICE LIST SECTION ══════════════════ */}
      {activeSection === 'invoices' && (
        <div className="bg-white rounded-xl border-2 border-gray-100 shadow-sm p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <SectionHeader title="Invoices" icon="FileText" />
            {/* Status filter */}
            <div className="flex flex-wrap gap-2">
              {['', 'Paid', 'Pending', 'Overdue', 'Partially Paid', 'Cancelled'].map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setInvoicePage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                    statusFilter === s
                      ? 'bg-[#2D869C] text-white border-[#2D869C]'
                      : 'border-gray-200 text-gray-600 hover:border-[#2D869C] hover:text-[#2D869C]'
                  }`}
                >
                  {s === '' ? 'All' : s}
                </button>
              ))}
            </div>
          </div>

          {invoiceLoading ? (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 animate-pulse rounded-lg h-14" />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Icon name="FileText" size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No invoices found for the selected criteria.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-3 text-gray-500 font-semibold">Invoice #</th>
                      <th className="text-left py-3 px-3 text-gray-500 font-semibold">Patient</th>
                      <th className="text-left py-3 px-3 text-gray-500 font-semibold">Date</th>
                      <th className="text-left py-3 px-3 text-gray-500 font-semibold">Type</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Grand Total</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Paid</th>
                      <th className="text-right py-3 px-3 text-gray-500 font-semibold">Due</th>
                      <th className="text-center py-3 px-3 text-gray-500 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const patientName = inv.patient
                        ? `${inv.patient.firstName || ''} ${inv.patient.lastName || ''}`.trim()
                        : 'Unknown';
                      const dateStr = inv.invoiceDate
                        ? new Date(inv.invoiceDate).toLocaleDateString('en-IN')
                        : '—';
                      return (
                        <tr key={String(inv._id)} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-3 font-semibold text-[#2D869C]">{inv.invoiceNumber}</td>
                          <td className="py-3 px-3">
                            <p className="font-medium text-gray-800">{patientName}</p>
                            {inv.patient?.mobile && (
                              <p className="text-xs text-gray-400">{inv.patient.mobile}</p>
                            )}
                          </td>
                          <td className="py-3 px-3 text-gray-600">{dateStr}</td>
                          <td className="py-3 px-3">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-medium">
                              {inv.billingType || 'Patient'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-right font-bold text-gray-900">{fmt(inv.grandTotal)}</td>
                          <td className="py-3 px-3 text-right text-green-700 font-semibold">{fmt(inv.amountPaid)}</td>
                          <td className="py-3 px-3 text-right text-yellow-700 font-semibold">{fmt(inv.amountDue)}</td>
                          <td className="py-3 px-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-bold ${
                                STATUS_COLORS[inv.status] || 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {invoicePagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Page {invoicePagination.page} of {invoicePagination.pages} &middot; {invoicePagination.total} total
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={invoicePage <= 1}
                      onClick={() => setInvoicePage((p) => p - 1)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border-2 border-gray-200 disabled:opacity-40 hover:border-[#2D869C] hover:text-[#2D869C] transition-colors"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(invoicePagination.pages, 5) }, (_, i) => {
                      const p = i + 1;
                      return (
                        <button
                          key={p}
                          onClick={() => setInvoicePage(p)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                            invoicePage === p
                              ? 'bg-[#2D869C] text-white border-[#2D869C]'
                              : 'border-gray-200 text-gray-600 hover:border-[#2D869C]'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button
                      disabled={invoicePage >= invoicePagination.pages}
                      onClick={() => setInvoicePage((p) => p + 1)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border-2 border-gray-200 disabled:opacity-40 hover:border-[#2D869C] hover:text-[#2D869C] transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FinanceBillingTab;
